import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useDataTable } from "@/hooks/use-data-table";
import { Head, router, useForm } from "@inertiajs/react";
import type { ColumnDef } from "@tanstack/react-table";
import { Building2, Camera, Loader2, PencilIcon, PlusIcon, TrashIcon } from "lucide-react";
import { slugify } from "@/lib/slugify";
import { type FormEvent, useMemo, useRef, useState } from "react";

type Organization = {
  id: number;
  name: string;
  slug: string;
  logo: string | null;
  usersCount: number;
  createdAt: string;
};

function CreateOrganizationDialog() {
  const [open, setOpen] = useState(false);
  const slugTouched = useRef(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const { data, setData, post, processing, reset } = useForm({
    organization: { name: "", slug: "", logo: null as File | null },
  });

  const logoPreview = data.organization.logo
    ? URL.createObjectURL(data.organization.logo)
    : null;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    post("/app/organizations", {
      forceFormData: true,
      onSuccess: () => {
        reset();
        slugTouched.current = false;
        setOpen(false);
      },
    });
  }

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setData("organization", { ...data.organization, logo: file });
    }
  }

  function handleNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    const name = e.target.value;
    if (slugTouched.current) {
      setData("organization", { ...data.organization, name });
    } else {
      setData("organization", { ...data.organization, name, slug: slugify(name) });
    }
  }

  function handleSlugChange(e: React.ChangeEvent<HTMLInputElement>) {
    const slug = e.target.value;
    slugTouched.current = slug !== "";
    setData("organization", { ...data.organization, slug });
  }

  function handleOpenChange(isOpen: boolean) {
    setOpen(isOpen);
    if (!isOpen) {
      reset();
      slugTouched.current = false;
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="font-normal">
          <PlusIcon />
          Nova Organização
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Criar Organização</DialogTitle>
            <DialogDescription>
              Adicione uma nova organização à plataforma.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <Field>
              <FieldLabel>Logo</FieldLabel>
              <div className="flex items-center gap-4">
                <div className="relative group">
                  <Avatar className="size-16 rounded-lg">
                    {logoPreview && <AvatarImage src={logoPreview} />}
                    <AvatarFallback className="rounded-lg text-lg">
                      <Building2 className="size-6 text-muted-foreground" />
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/50 opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <Camera className="size-4 text-white" />
                  </button>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoChange}
                  />
                </div>
              </div>
            </Field>
            <Field>
              <FieldLabel htmlFor="name">Nome</FieldLabel>
              <Input
                id="name"
                value={data.organization.name}
                onChange={handleNameChange}
                placeholder="Minha Organização"
                required
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="slug">Slug</FieldLabel>
              <Input
                id="slug"
                value={data.organization.slug}
                onChange={handleSlugChange}
                placeholder="minha-organizacao"
                required
              />
            </Field>
          </FieldGroup>
          <DialogFooter>
            <Button type="submit" disabled={processing}>
              {processing && (
                <Loader2 className="mr-2 size-4 animate-spin" />
              )}
              Criar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditOrganizationDialog({
  organization,
}: {
  organization: Organization;
}) {
  const [open, setOpen] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const { data, setData, patch, processing, errors, reset } = useForm({
    organization: {
      name: organization.name,
      logo: null as File | null,
    },
  });

  const logoPreview = data.organization.logo
    ? URL.createObjectURL(data.organization.logo)
    : organization.logo;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    patch(`/app/organizations/${organization.id}`, {
      forceFormData: true,
      onSuccess: () => {
        reset();
        setOpen(false);
      },
    });
  }

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setData("organization", { ...data.organization, logo: file });
    }
  }

  function handleOpenChange(isOpen: boolean) {
    setOpen(isOpen);
    if (isOpen) {
      reset();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <PencilIcon className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Editar Organização</DialogTitle>
            <DialogDescription>
              Atualize as informações da organização.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <Field>
              <FieldLabel>Logo</FieldLabel>
              <div className="flex items-center gap-4">
                <div className="relative group">
                  <Avatar className="size-16 rounded-lg">
                    <AvatarImage src={logoPreview ?? undefined} />
                    <AvatarFallback className="rounded-lg text-lg">
                      <Building2 className="size-6 text-muted-foreground" />
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/50 opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <Camera className="size-4 text-white" />
                  </button>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoChange}
                  />
                </div>
                <div className="text-sm">
                  <p className="font-medium">{organization.name}</p>
                  <p className="text-muted-foreground font-mono text-xs">
                    {organization.slug}
                  </p>
                </div>
              </div>
            </Field>
            <Field>
              <FieldLabel htmlFor={`edit-name-${organization.id}`}>
                Nome
              </FieldLabel>
              <Input
                id={`edit-name-${organization.id}`}
                value={data.organization.name}
                onChange={(e) =>
                  setData("organization", {
                    ...data.organization,
                    name: e.target.value,
                  })
                }
                required
              />
              {errors["organization.name"] && (
                <p className="text-destructive text-sm">
                  {errors["organization.name"]}
                </p>
              )}
            </Field>
            <Field>
              <FieldLabel>Slug</FieldLabel>
              <p className="text-sm font-mono text-muted-foreground">
                {organization.slug}
              </p>
            </Field>
          </FieldGroup>
          <DialogFooter>
            <Button type="submit" disabled={processing}>
              {processing && (
                <Loader2 className="mr-2 size-4 animate-spin" />
              )}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteOrganizationButton({ id }: { id: number }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => {
        if (confirm("Tem certeza que deseja excluir esta organização?")) {
          router.delete(`/app/organizations/${id}`);
        }
      }}
    >
      <TrashIcon className="size-4" />
    </Button>
  );
}

export default function Index({
  organizations,
}: {
  organizations: Organization[];
}) {
  const columns = useMemo<ColumnDef<Organization>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        enableColumnFilter: true,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Nome" />
        ),
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
        meta: {
          label: "Nome",
          variant: "text" as const,
          placeholder: "Filtrar por nome...",
        },
      },
      {
        id: "slug",
        accessorKey: "slug",
        enableColumnFilter: true,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Slug" />
        ),
        meta: {
          label: "Slug",
          variant: "text" as const,
          placeholder: "Filtrar por slug...",
        },
      },
      {
        id: "usersCount",
        accessorKey: "usersCount",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Usuários" />
        ),
        meta: {
          label: "Usuários",
        },
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Criado em" />
        ),
        cell: ({ row }) =>
          new Date(row.original.createdAt).toLocaleDateString("pt-BR"),
        meta: {
          label: "Criado em",
        },
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <EditOrganizationDialog organization={row.original} />
            <DeleteOrganizationButton id={row.original.id} />
          </div>
        ),
        size: 100,
      },
    ],
    [],
  );

  const { table } = useDataTable({
    data: organizations,
    columns,
    pageCount: Math.ceil(organizations.length / 10),
    enableRowSelection: false,
  });

  return (
    <>
      <Head title="Organizações" />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Organizações</h1>
        <p className="text-muted-foreground text-sm">
          Gerencie as organizações da plataforma.
        </p>
      </div>
      <DataTable table={table}>
        <DataTableToolbar table={table}>
          <CreateOrganizationDialog />
        </DataTableToolbar>
      </DataTable>
      </div>
    </>
  );
}
